<script>
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import Errors from './Errors';
import { flatten } from '@/utils/Helpers';
import { sequence } from '@bignerve/ui-utils';
import { deferredPromise } from '@/utils/promise';

export default {
    name: 'NDataForm',
    
    props: {
        action: {
            type: [String, Function],
        },
        method: {
            type: String,
            default: 'POST'
        },
        data: {
            type: Object,
            default: () => ({})
        },
        confirm: {
            type: Boolean,
            default: false
        },
        once: {
            type: Boolean,
            default: false
        },
        dirtable: {
            type: Array
        },
        prune: {
            type: Function,
            default: v => v
        },
        beforeSubmit: Function,
    },

    emits: ['change', 'before-send', 'after-send', 'submit', 'reset', 'error', 'success', 'modal-closed'],

    watch: {
        data: {
            handler(n, o) {
                this.fields = n;
            },
            deep: true
        },
        fields: {
            handler(n, o) {
                this.$emit('change', n);
            },
            deep: true
        }
    },

    provide() {
        return {
            form: this.form,
        };
    },

    data() {
        return {
            busy: false,
            success: false,
            hasErrors: false,
            errors: new Errors,
            fields: this.data || {},
            formFields: [],
            middlewares: new Map(),
            sendPromise: null,
        };
    },

    computed: {
        form() {
            return this;
        },
        dirty() {
            if (this.dirtable) {
                return this.dirtable.some(field => !isEmpty(
                    get(this.fields, field, null)
                ));
            }

            return Object.keys(flatten(this.fields)).length;
        }
    },

    methods: {
        submit() {
            if (this.once && (this.busy || this.success)) {
                return;
            }

            this.busy = false;
            this.success = false;
            this.errors.clear();
            this.hasErrors = false;
            this.sendPromise = deferredPromise();

            if (!this.validate()) {
                return {
                    success: false,
                    message: 'Validation failed',
                };
            }

            if (this.confirm) {
                this.$refs.dialog.open();

                return this.sendPromise.promise;
            }

            this.send();

            return this.sendPromise.promise;
        },
        async send () {
            if (this.confirm) {
                this.closeDialog()
            }

            if (!this.action) {
                console.warn('No action provided');

                this.sendPromise?.resolve({
                    success: false,
                    message: 'No action provided'
                });

                return;
            }

            this.busy = true;
            this.errors.clear();

            // triger middlewares
            await sequence(
                [...this.middlewares.values()],
                middleware => middleware(this.fields)
            );

            const candSend = this.beforeSubmit ? await this.beforeSubmit(this.fields) : true;

            if (!candSend) {
                this.sendPromise?.resolve({
                    success: false,
                    message: 'Before submit callback returned false'
                });

                return;
            }

            try {
                this.$emit('before-send', this.fields);

                const fields = this.prune(this.fields);

                const payload = await (typeof this.action === 'function'
                    ? this.action(fields)
                    : this.$nerve.requester.makeRequest(this.method, this.action, fields).then(r => r.data)
                );

                this.$emit('after-send', payload);

                this.onSuccess(payload);

                this.sendPromise?.resolve({
                    success: true,
                    payload,
                });
            } catch (error) {
                this.onError(error);

                this.sendPromise?.resolve({
                    success: false,
                    message: error.message,
                });

                throw error;
            } finally {
                this.$emit('submit');
            }
        },
        reset() {
            this.busy = false;
            this.success = false;
            this.fields = this.data;
            this.hasErrors = false;
            this.errors.clear();

            this.formFields.forEach(field => {
                field.reset();
            });
            
            this.$emit('reset');
        },
        onSuccess(data) {
            this.hasErrors = false;
            this.busy = false;
            this.success = true;
            this.errors.clear();
            this.$emit('success', data, this.fields);
        },
        onError(error) {
            let errors = get(error, 'response.data.errors', []);

            this.busy = false;
            this.success = false;
            this.hasErrors = true;
            this.errors.record(errors);
            this.$emit('error', errors);
        },
        closeDialog() {
            this.$refs.dialog.close();
        },
        validate() {
            const errors = this.formFields.filter(field => field.required && !field.modelValue)

            this.errors.record(errors.map(field => ({
                field: field.name,
                error: this.$t(
                    'component.form.error.required',
                    field.label || field.fieldName ? 0 : 1, { field: field.label || field.fieldName }
                )
            })));

            this.hasErrors = errors.length > 0;

            return !this.hasErrors;
        },
        handleConfirm() {
            this.send();
        },
        handleCancel() {
            this.$emit('modal-closed');

            this.sendPromise?.resolve({
                success: false,
                message: 'User cancelled the action'
            });
        },
    },
}
</script>

<template>
    <form @submit.prevent="submit" @keypress.enter.prevent>
        <NNotify
            v-if="confirm"
            ref="dialog"
            title="Confirm?"
            message="Do you want to continue with this action?"
            @cancel="handleCancel"
            @confirm="handleConfirm"
        />
        <slot
            :fields="fields"
            :errors="errors"
            :dirty="dirty"
            :busy="busy"
            :success="success"
            :submit="submit"
            :reset="reset"
        />
    </form>
</template>
